import { IGroupedOption } from "./../components/multiselect-dropdown/multiselect-dropdown.component";
import { SortOrder } from "app/services/helper.service";
import { EMPTY, Observable, of, throwError, firstValueFrom } from "rxjs";
import { catchError, map, shareReplay } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { CookieService } from "ngx-cookie-service";
import {
  Device,
  IDeviceInventoryFilters,
  ReplaceDeviceForm,
} from "../models/device.model";
import { BitParaSettingsRequest } from "../components/devices/device-settings/device-settings.component";
import { LockBeaconModel } from "app/models/lock-beacon.model";
import {
  ICompanyGroupCompany,
  IUpdateCompanyGroupBindingTimeForm,
} from "app/models/company.models";
import { CameraEvent, LockEvent } from "app/models/report.model";
import {
  AllResidentSorter,
  FlaggedResidentSorter,
  NewResidentSorter,
  ResidentCompany,
  ResidentDisplayName,
  SetResidentDevice,
  UnsetResidentDevice,
} from "app/models/resident.model";
import { NewStaff } from "../models/newstaff.model";
import {
  IAlarmReasonCategory,
  IAlarmReasonsRequest,
} from "app/models/alarm-reasons.model";
import {
  CameraGroupEditForm,
  ICameraGroup,
  IVisitConfiguration,
  NewCameraGroupForm,
  VisitOnAlertConfig,
} from "app/models/camera.model";
import {
  CareRecipientCard,
  CareRecipientStatistics,
  IAlertStatistics,
  IEmaacStatistics,
  ICareRecipientCardUserLimit,
  StatisticsFilters,
  ICareRecipientCardNote,
  IActiveUsersStats,
} from "app/models/statistics.model";
import { IResidentList } from "app/components/landing-page/new-residents/new-residents-interfaces";
import { IDeviceHistory } from "app/models/device-history.model";
import { IUpdateDeviceBindTimeForm } from "app/components/landing-page/devices/device-information/device-bind-time-manager/device-bind-time-manager.component";
import { IActivityLog, ActivityLogFilters, IRemovedScheduledVisit } from "app/models/activity-log.model";
import { IEverWatchForm } from "app/components/everwatch-form/everwatch-form.component";
import { INightowlForm } from "app/components/nightowl-form/nightowl-form.component";
import { IScheduledVisitLog } from "./camera-schedule.service";
import { BindingTimeConfig } from "app/components/landing-page/devices/devices.component";
import { EmaNotification } from "app/models/notification.model";
import { AlertHistory } from "app/models/alert-history";
import { CustomerRule, CustomerRuleUpdate } from "app/models/customer-rule";
import { CallListItem, CustomerCallListUpdate } from "app/models/call-list-item";
import { NfcEventRequest } from "app/models/nfc-event-state";
import { ConnectDevice } from "app/models/connect-device";
import { CreateLandingPageUserRequest } from "app/models/staff.model";

/**
 *  Class for every API call.
 *  Authorization headers are intercept for every request automatically in interceptor.service.ts file.
 */

@Injectable()
export class ApiService {
  constructor(private http: HttpClient, private cookies: CookieService) { }

  private lockBeaconConfigCache = new Map<string, Observable<any>>();

  /**
   * LOGIN API CALLS
   */

  login(data: any): Observable<any> {
    const url = "/api/login";
    return this.http.post(url, data);
  }

  logout(): Observable<boolean> {
    this.cookies.deleteAll("/", undefined, false, "Lax");
    // Get language from the localstorage before clearing it
    const language = localStorage.getItem("language");
    localStorage.clear();
    // Set language back to local storage after everything else has been cleared
    localStorage.setItem("language", language);
    return of(true);
  }

  logoutAuthenticatedUser(landingPage = false): Observable<any> {
    const url = "/api/logoutFederation/" + landingPage;
    return this.http.get(url);
  }

  logLogoutEvent(): Observable<any> {
    const url = "/api/logLogoutEvent/Portal";
    return this.http.get(url);
  }

  isAuth(): Observable<any> {
    if (!this.cookies.get("session-token")) {
      const err = "Token not found";
      return throwError(() => err);
    }
    const url = "/api/isAuth";
    return this.http.get(url);
  }

  isAuthSSO(): Observable<any> {
    if (!this.cookies.get("session-token")) {
      const err = "Token not found";
      return throwError(() => err);
    }
    const url = "/api/isAuth/SSO";
    return this.http.get(url);
  }

  isRole(roles: any[]): Observable<any> {
    if (!this.cookies.get("session-token")) {
      const err = "Token not found";
      return throwError(() => err);
    }
    const url = "/api/isRole";
    return this.http.post(url, roles);
  }

  isRoleLevel(role: any): Observable<any> {
    if (!this.cookies.get("session-token")) {
      const err = "Token not found";
      return throwError(() => err);
    }
    const url = "/api/isRoleLevel/" + role;
    return this.http.get(url);
  }

  isSSORole(role: any): Observable<any> {
    if (!this.cookies.get("session-token")) {
      const err = "Token not found";
      return throwError(() => err);
    }
    const url = "/api/isSSORole/" + role;
    return this.http.get(url);
  }

  startFederationLogin(data: any): Observable<any> {
    const url = "/api/login/federation/start";
    return this.http.post(url, data);
  }

  getFederationSession(data: any): Observable<any> {
    const url = "/api/login/federation/session";
    return this.http.post(url, data);
  }
  getFederationSessionDemo(): Observable<any> {
    const url = "/api/login/federation/sessiondemo";
    return this.http.get(url);
  }

  loginToFederationCustomer(data: any): Observable<any> {
    const url = "/api/login/federation/customer";
    return this.http.post(url, data);
  }

  /**
   * DISPLAY API CALLS
   */

  getDisplays(): Observable<any> {
    const url = "/api/Display";
    return this.http.get(url);
  }

  getDisplayEvents(id: number): Observable<any> {
    const url = "/api/Display/" + id;
    return this.http.get(url);
  }

  getSingleDisplayParameters(id: number): Observable<any> {
    const url = "/api/Display/" + id + "/Parameters";
    return this.http.get(url);
  }

  deleteDisplayEvent(rowId: number): Observable<any> {
    const url = "/api/Display/" + rowId;
    return this.http.delete(url);
  }

  saveDisplayEvent(data: any, isNew: boolean): Observable<any> {
    const url = "/api/Display";
    if (isNew) {
      return this.http.put(url, data);
    } else {
      return this.http.post(url, data);
    }
  }

  saveDisplayParameters(data: any): Observable<any> {
    const url = "/api/Display/Parameters";
    return this.http.post(url, data);
  }

  /**
   * DEVICE API CALLS
   */

  getDevices(data: any): Observable<any> {
    const url = "/api/Device/List";
    return this.http.post(url, data);
  }

  // Use this for getting devices faster, but with less information
  getDevicesWithBasicInfo(): Observable<Device[]> {
    const url = "/api/Device/BasicInfoList";
    return this.http.get<Device[]>(url);
  }

  generateExternalPassword(id: string): Observable<any> {
    const url = "/api/Device/GenerateExternalSystemPassword/" + id;
    return this.http.get(url);
  }

  /*
  getDeviceRegistry(): Observable<any> {
    let url = 'api/DeviceRegistry';
    return this.http.get(url);
  }
  */

  getOnlineStatus(getAll: boolean): Observable<any> {
    let url = "";
    if (getAll) {
      url = "/api/Device/Status/All";
    } else {
      url = "/api/Device/Status";
    }
    return this.http.get(url);
  }

  getOnlineStatusBases(): Observable<any> {
    let url = "";
    url = "/api/Device/Status/Bases";
    return this.http.get(url);
  }

  getOnlineStatusDevices(statusType: string): Observable<any> {
    const url = "/api/Device/Status/" + statusType;
    return this.http.get(url);
  }

  getOnlineStatusCompanyGroup(): Observable<any> {
    return this.http.get("/api/Device/Status/CompanyGroup");
  }

  getOnlineStatusCompanyGroupDevices(statusType: string): Observable<any> {
    return this.http.get("/api/Device/Status/CompanyGroup/" + statusType);
  }

  deleteDeviceExclusion(deviceId: string): Observable<any> {
    const url = `/api/Device/${deviceId}/Exclusion`;
    return this.http.delete(url);
  }

  deleteDeviceExclusions(deviceIds: string[]): Observable<any> {
    const url = `/api/Device/Exclusion`;
    return this.http.delete(url, { body: deviceIds });
  }

  setDeviceExclusion(deviceId: string, exclusionReason: number): Observable<any> {
    const url = `/api/Device/Exclusion`;
    return this.http.post(url, [{ deviceId, exclusionReason }]);
  }

  setDeviceExclusions(items: { deviceId: string; exclusionReason: number }[]): Observable<any> {
    const url = `/api/Device/Exclusion`;
    return this.http.post(url, items);
  }

  getExcludedDevicesByLocation(locationId: string): Observable<any> {
    const url = `/api/Device/Status/Excluded?locationId=${locationId}`;
    return this.http.get(url);
  }

  getActivationTranslations(language: string): Observable<any> {
    const url = "/api/Device/Activations/" + language;
    return this.http.get(url);
  }

  getDeviceTranslations(lang: string): Observable<any> {
    const url = "/api/Device/Translations/" + lang;
    return this.http.get(url);
  }

  getDeviceTranslationsForAuthenticatedUser(lang: string): Observable<any> {
    const url = `/api/Device/Translations/${lang}/AuthenticatedUser`;
    return this.http.get(url);
  }

  getDeviceInfo(devId: string): Observable<any> {
    const url = "/api/Device/" + devId;
    return this.http.get(url);
  }

  updateDevice(data: Device): Observable<any> {
    const url = "/api/Device/";
    return this.http.post(url, data);
  }

  deleteDevice(id: string): Observable<any> {
    const url = "/api/Device/" + id;
    return this.http.delete(url);
  }

  unmuteDevice(id: string): Observable<any> {
    const url = `/api/Device/${id}/unmute`;
    return this.http.post(url, { muted: false });
  }

  cancelDeleteDevice(id: string): Observable<any> {
    const url = "/api/Device/CancelDelete/" + id;
    return this.http.post(url, id);
  }

  forceDeleteDevice(id: string): Observable<any> {
    const url = "/api/Device/Force/" + id;
    return this.http.delete(url);
  }

  retransferSettings(id: string): Observable<any> {
    const url = "/api/Device/Retransfer/" + id;
    return this.http.get(url);
  }

  cancelSettingTransfer(id: string): Observable<any> {
    const url = "/api/Device/SettingTransfer/" + id;
    return this.http.delete(url);
  }

  checkDeviceValidity(id: string): Observable<any> {
    const url = "/api/Device/isFree/" + id;
    return this.http.get(url);
  }

  addDevice(data: Device): Observable<any> {
    const url = "/api/Device/";
    return this.http.put(url, data);
  }

  replaceDevice(data: ReplaceDeviceForm): Observable<any> {
    const url = "/api/Device/ReplaceDevice";
    return this.http.post(url, data);
  }

  replaceDoorId(deviceId: string, deviceName: string, newDoorId: string, defectiveDoorId: string, serverName: string): Observable<any> {
    this.lockBeaconConfigCache.delete(deviceId);
    const url = "/api/Device/ReplaceDoorId";
    return this.http.post(url, { deviceId, deviceName, newDoorId, defectiveDoorId, serverName });
  }

  cancelReplace(id: string): Observable<any> {
    const url = "/api/Device/CancelReplace/" + id;
    return this.http.get(url);
  }

  saveDeviceBitPara(data: BitParaSettingsRequest): Observable<any> {
    const url = "/api/Device/SaveBitPara";
    return this.http.post(url, data);
  }

  saveAdvancedSettings(data: any): Observable<any> {
    const url = "/api/Device/SaveAdvancedSettings";
    return this.http.post(url, data);
  }

  saveDeviceNetworkSettings(data: any): Observable<any> {
    const url = "/api/Device/SaveNetworkSettings";
    return this.http.post(url, data);
  }

  getDeviceFirmwareVersions(device: string): Observable<any> {
    const url = "/api/Device/FirmwareUpdate/" + device;
    return this.http.get(url);
  }

  startFirmwareUpdate(data: any): Observable<any> {
    const url = "/api/Device/FirmwareUpdate";
    return this.http.put(url, data);
  }

  cancelFirmwareUpdate(id: string): Observable<any> {
    const url = "/api/Device/FirmwareUpdate/" + id;
    return this.http.delete(url);
  }

  getDeviceNameList(idList: string[]): Observable<any> {
    const url = "/api/Device/NameList";
    return this.http.post(url, idList);
  }

  getCompanyGroupDeviceInventory(
    data: IDeviceInventoryFilters
  ): Observable<any> {
    const url = "/api/Device/CompanyGroupInventory";
    return this.http.post(url, data);
  }

  getCompanyGroupInventorySummary(): Observable<any> {
    const url = "/api/Device/CompanyGroupInventorySummary";
    return this.http.get(url);
  }

  getCompanyInventory(customerId: string): Observable<any> {
    const url = "/api/Device/CompanyInventory/" + customerId;
    return this.http.get(url);
  }

  saveBluetoothSettings(data: any): Observable<any> {
    const url = "/api/Device/SaveBluetoothSettings";
    return this.http.post(url, data);
  }

  saveSonixxSettings(data: any, deviceId: string): Observable<any> {
    const url = "/api/Device/SaveSonixxSettings/" + deviceId;
    return this.http.post(url, data);
  }

  uploadLockCsv(data: any): Observable<any> {
    const url = "/api/LockDevice/csv";
    return this.http.post(url, data);
  }

  getDeviceTypeSubClasses(type: string): Observable<any> {
    const url = "/api/Device/SubClass/" + type;
    return this.http.get(url);
  }

  getDeviceInventoryMovementHistory(deviceId: string): Observable<any> {
    const url = "/api/Device/DeviceInventoryMovementHistory/" + deviceId;
    return this.http.get(url);
  }

  uploadDeviceInventoryCsv(data: any): Observable<any> {
    const url = "/api/Device/AddViaCsv";
    return this.http.post(url, data);
  }

  addDeviceToInventory(data: any): Observable<any> {
    const url = "/api/Device/Inventory";
    return this.http.post(url, data);
  }

  addDeviceToCustomerInventory(data: any): Observable<any> {
    const url = "/api/Device/CustomerInventory";
    return this.http.post(url, data);
  }

  updateBaseDeviceLocation(
    device: any,
    companyLocationChanged: boolean
  ): Observable<any> {
    const url = companyLocationChanged
      ? void 0 //"/api/Device/UpdateBaseDeviceCompanyLocation"
      : "/api/Device/UpdateBaseDeviceLocation";
    return this.http.post(url, device);
  }

  getDeviceBatteryStatus(deviceId: string): Observable<number> {
    const url = "/api/Device/BatteryStatus/" + deviceId;
    return this.http.get<number>(url);
  }

  updateDeviceBindTime(form: IUpdateDeviceBindTimeForm): Observable<void> {
    const url = "/api/Device/UpdateBindTime";
    return this.http.put<void>(url, form);
  }

  getNightowlCustomerId(): Observable<string | Array<{ id: string, name: string }>> {
    const url = "/api/Device/NightowlServiceCustomerId";
    return this.http.get<string | Array<{ id: string, name: string }>>(url);
  }

  saveNightowlCustomerId(externalSystemIds: Array<{ id: string, name: string }>): Observable<void> {
    const url = "/api/Device/NightowlServiceCustomerId";
    return this.http.post<void>(url, { externalSystemIds });
  }

  // CONNECT DEVICES TO NFC TAG
  getConnectedDevices(tagId: string): Observable<ConnectDevice[]> {
    return this.http.get<ConnectDevice[]>(`/api/Nfc/${tagId}/connectedDevices`);
  }

  addConnectedDevice(tagId: string, payload: ConnectDevice): Observable<void> {
    return this.http.post<void>(`/api/Nfc/${tagId}/connectedDevices`, payload);
  }

  removeConnectedDevice(tagId: string, deviceId: string): Observable<void> {
    return this.http.delete<void>(`/api/Nfc/${tagId}/connectedDevices/${deviceId}`);
  }

  // DEVICE HISTORY API CALLS
  getDeviceHistory(deviceId: string): Observable<IDeviceHistory> {
    const url = "/api/DeviceHistory/AuthenticatedUser/" + deviceId;
    return this.http.get<IDeviceHistory>(url);
  }

  // CAMERA API CALLS
  getCameraGroups(): Observable<any> {
    const url = "/api/Camera/CameraGroups/List";
    return this.http.get(url);
  }

  addCameraGroup(data: {
    cameraGroup: NewCameraGroupForm;
    visitConfiguration: IVisitConfiguration | undefined;
  }): Observable<any> {
    const url = "/api/Camera/AddCameraGroup";
    return this.http.post(url, data);
  }

  updateCameraGroup(data: {
    oldCameraGroupData: ICameraGroup;
    oldVisitConfigData: VisitOnAlertConfig;
    cameraGroupToUpdate: CameraGroupEditForm;
    visitConfigToUpdate: VisitOnAlertConfig;
    alertDevices: Record<string, string>;
    removedVisits: IRemovedScheduledVisit[];
    deviceType: string;
  }): Observable<any> {
    const url = "/api/Camera/EditCameraGroup";
    return this.http.post(url, data);
  }

  deleteCameraGroup(data: {
    cameraGroupId: string,
    cameraGroupName: string,
    visitConfigId: string,
    removedVisits: IRemovedScheduledVisit[],
    deviceType: string,
  }): Observable<boolean> {
    const url = `/api/Camera/DeleteCameraGroup`;
    return this.http.post<boolean>(url, data);
  }

  isNightowlServiceCustomerIdSet(): Observable<boolean> {
    const url = `/api/Camera/NightowlExternalIdSet`;
    return this.http.get<boolean>(url);
  }

  saveNightowlVisit(data: any[]): Observable<any> {
    const url = `/api/Camera/AddNightowlScheduledVisit`;
    return this.http.post<void>(url, data);
  }

  getNightowlVisits(): Observable<any> {
    const url = `/api/Camera/GetNightowlVisits`;
    return this.http.get<any>(url);
  }

  deleteNightowlVisit(id: string) {
    const url = `/api/Camera/DeleteNightowlVisit/${id}`;
    return this.http.delete(url).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error(`Error deleting nightowl visit with ID ${id}:`, error);
        return throwError(() => new Error('Failed to delete nightowl visit'));
      })
    );
  }

  editScheduledVisit(data: {
    visitScheduleId: string;
    startTime: string;
    duration: number;
    gracePeriod?: number | null;
    deviceType: string;
    cameraGroupId: string;
    cameraGroupName: string;
    day?: number;
    oldStartTime: string;
    oldDuration: number;
    oldGracePeriod: number | null;
  }): Observable<any> {
    return this.http.put('/api/Camera/EditScheduledVisit', data);
  }

  resumeNightowlDevices(data: {
    cameraGroupId: string;
    devices: Array<{ externalSystemId: string; companyExternalSystemId: string }>;
    visitScheduleId?: string;
  }): Observable<any> {
    const url = `/api/Camera/ResumeNightowlDevices`;
    return this.http.post(url, data);
  }

  logScheduledVisitActivities(data: IScheduledVisitLog[]): Observable<any> {
    if (data?.length === 0) return EMPTY;

    // Validate and remove invalid records
    data.forEach((record, index) => {
      if (record.action === "Authentication" || record.action === "Modify") {
        const removed = data.splice(index, 1);
        console.warn(`Invalid action type '${removed[0].action}' for scheduled visit log: ${JSON.stringify(removed[0])}`);
      }
    });

    return this.http.post("/api/Camera/LogScheduledVisitActivity", { logRequests: data });
  }

  // EVERWATCH API CALLS
  getEverWatchAvailability(everWatchId: string): Observable<{ available: boolean, lyraId?: string }> {
    const url = "/api/EverWatch/IsFree/" + everWatchId;
    return this.http.get<any>(url);
  }

  addEverWatch(form: IEverWatchForm): Observable<any> {
    const url = "/api/EverWatch";
    return this.http.post(url, form);
  }

  // NIGHTOWL API CALLS
  getNightowlAvailability(nightOwlId: string): Observable<{ available: boolean, lyraId?: string }> {
    const url = "/api/Nightowl/IsFree/" + nightOwlId;
    return this.http.get<any>(url);
  }

  saveNightowl(form: INightowlForm): Observable<any> {
    const url = "/api/Nightowl";
    return this.http.post(url, form);
  }

  /**
   * LOCATION API CALLS
   */
  getLocations(): Observable<any> {
    const url = "/api/location";
    return this.http.get(url);
  }

  getLocation(id: string): Observable<any> {
    const url = "/api/Location/" + id;
    return this.http.get(url);
  }

  updateLocation(data: any): Observable<any> {
    const url = "/api/location";
    return this.http.post(url, data);
  }

  addNewLocation(data: any): Observable<any> {
    const url = "/api/location";
    return this.http.put(url, data);
  }

  confirmLocationDelete(id: string): Observable<any> {
    const url = "/api/Location/ConfirmDelete/" + id;
    return this.http.get(url);
  }

  deleteLocation(id: string): Observable<any> {
    const url = "/api/Location/" + id;
    return this.http.delete(url);
  }

  getEmaSettings(id: string): Observable<any> {
    const url = "/api/Ema/Settings/" + id;
    return this.http.get(url);
  }

  saveEmaSettings(data: any): Observable<any> {
    const url = "/api/Ema/Settings";
    return this.http.post(url, data);
  }

  getLocationStaffList(id: string): Observable<any> {
    const url = "/api/Location/PersonnelList/" + id;
    return this.http.get(url);
  }

  updateLocationStaffList(data: any): Observable<any> {
    const url = "/api/Location/PersonnelList";
    return this.http.post(url, data);
  }

  getCompanyGroupLocations(): Observable<any> {
    const url = "/api/Location/CompanyGroupLocations";
    return this.http.get(url);
  }

  /**
   * COMPANY API CALLS
   */

  getFeatures(getOnlyEnabled: boolean): Observable<any> {
    const url = getOnlyEnabled
      ? "/api/Customer/Features"
      : "/api/Customer/Features/All";
    return this.http.get(url);
  }

  getCustomerAliasFeatures(customerIds: string[]): Observable<any> {
    const url = "/api/Customer/FeaturesByAliases";
    return this.http.get(url, { params: { 'customerIds': customerIds } });
  }

  submitFeatures(features: string[]): Observable<any> {
    const url = "/api/Customer/Features";
    return this.http.post(url, features);
  }

  getCustomerInformation(): Observable<any> {
    const url = "/api/Customer";
    return this.http.get(url);
  }

  submitCustomerInformation(data: any): Observable<any> {
    const url = "/api/Customer";
    return this.http.post(url, data);
  }

  enableTwoFactorAuthentication(): Observable<any> {
    const url = "/api/Customer/EnableTwoFactorAuthentication";
    return this.http.get(url);
  }

  disableTwoFactorAuthentication(): Observable<any> {
    const url = "/api/Customer/DisableTwoFactorAuthentication";
    return this.http.get(url);
  }

  confirmTwoFactorCode(code: number): Observable<any> {
    const url = "/api/Customer/ConfirmTwoFactorAuthentication/" + code;
    return this.http.get(url);
  }

  /**
   * NOTIFICATION API CALLS
   */

  getNotifications(
    lang: string,
    amount: number,
    highlights: boolean,
    serverName?: string
  ): Observable<any> {
    let url = "";
    if (highlights) {
      url = "/api/Notification/Highlights/" + lang + "/" + amount;
    } else if (serverName) {
      url = "/api/Notification/" + lang + "/" + amount + "/" + serverName;
    } else {
      url = "/api/Notification/" + lang + "/" + amount;
    }
    return this.http.get(url);
  }

  getAllNotificationsForAdmin(lang: string, amount: number): Observable<any> {
    return this.http.get("/api/Notification/" + lang + "/" + amount);
  }

  submitNotification(data: any[], edit: boolean) {
    const url = "/api/Notification";
    if (edit) {
      return this.http.post(url, data);
    } else {
      return this.http.put(url, data);
    }
  }

  deleteNotification(batchId: string): Observable<any> {
    const url = "/api/Notification/" + batchId;
    return this.http.delete(url);
  }

  getEmaNotifications(includeExpired: boolean = false): Observable<any> {
    const url = `/api/Notification/EMA?includeExpired=${includeExpired}`;
    return this.http.get(url);
  }

  submitEmaNotification(data: EmaNotification): Observable<any> {
    const url = "/api/Notification/EMA";
    return this.http.post(url, data);
  }

  /**
   * STATISTICS API CALLS
   */

  getAlertStatistics(filters: StatisticsFilters, page: number): Observable<IAlertStatistics> {
    const url = `/api/Statistics/Alerts/${page}`;
    return this.http.post<IAlertStatistics>(url, filters);
  }

  getAlarmCenterStatistics(
    filters: StatisticsFilters, page: number
  ): Observable<IEmaacStatistics> {
    const url = `/api/Statistics/Emaac/${page}`;
    return this.http.post<IEmaacStatistics>(url, filters);
  }

  getCareRecipientStatisticsCards(
    filters: StatisticsFilters
  ): Observable<CareRecipientStatistics[][]> {
    const url = "/api/Statistics/CareRecipients";
    return this.http.post<CareRecipientStatistics[][]>(url, filters);
  }

  checkCareRecipientStatisticsCardUserLimit(): Observable<ICareRecipientCardUserLimit> {
    const url = "/api/Statistics/CareRecipients/UserLimit";
    return this.http.get<ICareRecipientCardUserLimit>(url);
  }

  /**
   * Request for adding note to care recipient statistics card
   * @param cardId ID of card / mongo document ID
   * @param note Note data
   * @returns True if note was added, else false
   */
  addNoteToCareRecipientStatisticsCard(
    cardId: string,
    note: ICareRecipientCardNote
  ): Observable<boolean> {
    const url = "/api/Statistics/CareRecipient/" + cardId + "/AddNote";
    return this.http.put<boolean>(url, note);
  }

  /**
   * Request for adding new care recipient statistics card
   * @param config New care recipient statistics card to add
   * @returns True if add success, else false
   */
  createCareRecipientStatisticsCard(
    config: CareRecipientCard
  ): Observable<boolean> {
    const url = "/api/Statistics/CareRecipient/Add";
    return this.http.put<boolean>(url, config);
  }

  /**
   * Request for updating care recipient statistics card
   * @param config Card update data
   * @returns True if update success, else false
   */
  updateCareRecipientStatisticsCard(
    config: CareRecipientCard
  ): Observable<boolean> {
    const url = "/api/Statistics/CareRecipient/Update";
    return this.http.put<boolean>(url, config);
  }

  /**
   * Request for deleting care recipient statistics card
   * @param cardId ID of card / mongo document ID
   * @returns True if delete success, else false
   */
  deleteCareRecipientStatisticsCard(cardId: string): Observable<boolean> {
    const url = "/api/Statistics/CareRecipient/Delete/" + cardId;
    return this.http.delete<boolean>(url);
  }

  getActiveUsersStats(filters: StatisticsFilters): Observable<IActiveUsersStats> {
    const url = "/api/Statistics/ActiveUsers";
    return this.http.post<IActiveUsersStats>(url, filters);
  }

  /**
   * REPORT API CALLS
   */

  getReportData(data: any, dataType: string): Observable<any> {
    let url = "";
    switch (dataType) {
      case "stats":
        url = "/api/Report/AlertStats";
        break;
      case "trend":
        url = "/api/Report/AlertTrends";
        break;
      case "ema":
        url = "/api/Report/EmaGraph";
        break;
      default:
        url = "/api/Report/AlertStats";
        break;
    }
    return this.http.post(url, data);
  }

  getReportFilteringOptions(): Observable<any> {
    const url = "/api/Report/FilteringOptions";
    return this.http.get(url);
  }

  getAlertHistory(data: any): Observable<any> {
    const url = "/api/Report/AlertHistory";
    return this.http.post(url, data);
  }

  getAlertById(alertId: string): Observable<any> {
    const url = `/api/Report/AlertById/${alertId}`;
    return this.http.get(url);
  }

  getCameraVisitHistory(data: any): Observable<CameraEvent[]> {
    const url = "/api/Report/CameraVisitHistory";
    return this.http.post<CameraEvent[]>(url, data);
  }

  getCameraVisitByAlertId(alertId: string): Observable<CameraEvent | CameraEvent[]> {
    const url = `/api/Report/CameraVisitByAlertId/${alertId}`;
    return this.http.get<CameraEvent | CameraEvent[]>(url);
  }

  getLockingEventHistory(data: any): Observable<LockEvent[]> {
    const url = "/api/Report/LockingEventHistory";
    return this.http.post<LockEvent[]>(url, data);
  }

  getActiveAlerts(getAll: boolean = false): Observable<any> {
    let url;
    if (getAll) {
      url = "/api/Admin/OldAlerts";
    } else {
      url = "/api/Report/ActiveAlerts";
    }
    return this.http.get(url);
  }

  getReportSummary(data: any): Observable<any> {
    const url = "/api/Report/AlertSummary";
    return this.http.post(url, data);
  }

  getEmaacStatistics(data: any): Observable<any> {
    const url = "/api/Report/EmaacStatistics";
    return this.http.post(url, data);
  }

  cancelActiveAlert(taskId: string, server: string = ""): Observable<any> {
    let url;
    if (server) {
      url = "/api/Report/CancelAlert/" + taskId + "/" + server;
    } else {
      url = "/api/Report/CancelAlert/" + taskId;
    }
    return this.http.get(url);
  }

  triggerPDFGeneration(data: any): Promise<any> {
    const url = "/api/Report/Pdf/AlertReport";
    return this.http.post(url, data).toPromise();
  }

  parseErrorBlob(err: HttpErrorResponse): Observable<any> {
    const reader: FileReader = new FileReader();

    const obs = Observable.create((observer: any) => {
      reader.onloadend = (e) => {
        observer.error(JSON.parse(reader.result.toString()));
        observer.complete();
      };
    });
    reader.readAsText(err.error);
    return obs;
  }

  downloadReport(reportId: string): Promise<any> {
    const url = "/api/report/Pdf/" + reportId;
    return firstValueFrom(
      this.http
        .get(url, { responseType: "blob" })
        .pipe(catchError(this.parseErrorBlob))
    );
  }

  addNote(data: any): Observable<any> {
    const url = "/api/Ema/Alarm/Comment";
    return this.http.put(url, data);
  }

  /**
   * ACTIVITY API CALLS
   */

  loadActivityData(deviceId: string, type: string): Observable<any> {
    const url = "/api/ActivityData/" + deviceId + "/" + type;
    return this.http.get(url);
  }

  getActivitySensors(): Observable<any> {
    const url = "/api/ActivityData/Devices";
    return this.http.get(url);
  }

  /**
   * ADMIN API CALLS
   */

  getAllCustomers(): Observable<any> {
    const url = "/api/Admin/Customers";
    return this.http.get(url);
  }

  loginAsCustomer(data: any): Observable<any> {
    const url = "/api/Login/Admin";
    return this.http.post(url, data);
  }

  ssoAdminLogin(data: { companyGroupId: string, server: string }): Observable<{ customers: any[], token: string, user: any }> {
    const url = "/api/Login/Admin/SSO";
    return this.http.post<any>(url, data);
  }

  search(type: string, keyword: string): Observable<any> {
    const url = "/api/Admin/Search/" + type + "/" + keyword;
    return this.http.get(url);
  }

  getNetworkFailures(): Observable<any> {
    const url = "/api/Admin/NetworkFailures";
    return this.http.get(url);
  }

  setFirmWare(data: any, editMode: boolean): Observable<any> {
    const url = "/api/Software";
    if (!editMode) {
      return this.http.put(url, data);
    } else {
      return this.http.post(url, data);
    }
  }

  getFirmwares(data: any): Observable<any> {
    const url = "/api/Software/SwVersions";
    return this.http.post(url, data);
  }

  deleteFirmware(id: number): Observable<any> {
    const url = "/api/Software/" + id;
    return this.http.delete(url);
  }

  getTestResults(id: string): Observable<any> {
    const url = "/api/Admin/TestResults/" + id;
    return this.http.get(url);
  }

  getSystemUsers(): Observable<any> {
    const url = "/api/Admin/SystemUsers";
    return this.http.get(url);
  }

  updateSystemUser(data: any): Observable<any> {
    const url = "/api/Admin/SystemUsers";
    return this.http.post(url, data);
  }

  addSystemUser(data: any): Observable<any> {
    const url = "/api/Admin/SystemUsers";
    return this.http.put(url, data);
  }

  deleteSystemUer(id: string): Observable<any> {
    const url = "/api/Admin/SystemUsers/" + id;
    return this.http.delete(url);
  }

  getAvailableServers() {
    const url = "/api/Admin/Servers";
    return this.http.get<string[]>(url);
  }

  /**
   * NFC
   */

  /**
   * Move NFC tag to the another base station
   * @param devId NFC tag's device ID
   * @param newBaseId New basestation ID for the NFC tag
   */
  moveNfcTag(devId: string, newBaseId: string): Observable<any> {
    const url = "/api/Nfc/MoveTag";
    return this.http.post(url, {
      deviceId: devId,
      newBaseStationId: newBaseId,
    });
  }

  /**
   * ANNA PERENNA
   */

  getMonitors(): Observable<any> {
    const url = "/api/AnnaPerenna/Monitors";
    return this.http.get(url);
  }

  getMonitorInfo(id: string): Observable<any> {
    const url = "/api/AnnaPerenna/Monitors/" + id;
    return this.http.get(url);
  }

  getMonitorStates(data: any): Observable<any> {
    const url = "/api/AnnaPerenna/Monitors/States";
    return this.http.post(url, data);
  }

  getMonitorProfiles(): Observable<any> {
    const url = "/api/AnnaPerenna/Profiles";
    return this.http.get(url);
  }

  updateProfileData(data: any): Observable<any> {
    const url = "/api/AnnaPerenna/Profiles";
    return this.http.post(url, data);
  }

  saveNewProfile(data: any): Observable<any> {
    const url = "/api/AnnaPerenna/Profiles";
    return this.http.put(url, data);
  }

  deleteMonitorProfile(id: string): Observable<any> {
    const url = "/api/AnnaPerenna/Profiles/" + id;
    return this.http.delete(url);
  }

  updateMonitorProfile(data: any): Observable<any> {
    const url = "/api/AnnaPerenna/Monitors/Profile";
    return this.http.post(url, data);
  }

  getMonitorZoneData(id: string): Observable<any> {
    const url = "/api/AnnaPerenna/Monitors/Zones/" + id;
    return this.http.get(url);
  }

  updateZone(data: any): Observable<any> {
    const url = "/api/AnnaPerenna/Monitors/Zones";
    return this.http.post(url, data);
  }

  /**
   * STAFF MANAGEMENT
   */

  getStaffList(): Observable<any> {
    const url = "/api/Personnel";
    return this.http.get(url);
  }

  getStaffInfo(id: string): Observable<any> {
    const url = "/api/Personnel/" + id;
    return this.http.get(url);
  }

  saveStaffInfo(data: any, newUuid: string): Observable<any> {
    const url = "/api/Personnel";
    const transactionId = new HttpParams().append("transactionId", newUuid);
    return this.http.post(url, data, { params: transactionId });
  }

  /**
   * @param username
   * @returns Returns false if the user name is already in use, true if user name is unique
   */
  checkUsername(username: string): Observable<any> {
    const url = "/api/Personnel/CheckUsername/" + username;
    return this.http.get(url);
  }

  addNewStaff(data: any): Observable<any> {
    const url = "/api/Personnel";
    return this.http.put(url, data);
  }

  deleteStaff(id: string): Observable<any> {
    const url = "/api/Personnel/" + id;
    return this.http.delete(url);
  }

  changePassword(data: any): Observable<any> {
    const url = "/api/Personnel/Password";
    return this.http.post(url, data);
  }

  getIdentityList(): Observable<any> {
    const url = "/api/Personnel/Identities";
    return this.http.get(url);
  }

  updateAuthenticatedUser(data: any, newUuid: string): Observable<any> {
    const url = "/api/Personnel/Identities/Link";
    const transactionId = new HttpParams().append("transactionId", newUuid);
    return this.http.post(url, data, { params: transactionId });
  }

  linkNonFederatedUser(data: any, newUuid: string): Observable<any> {
    const url = "/api/Personnel/Identities/LinkNonFederated";
    const transactionId = new HttpParams().append("transactionId", newUuid);
    return this.http.post(url, data, { params: transactionId });
  }

  saveStaffLocationList(data: any, newUuid: string): Observable<any> {
    const url = "/api/Personnel/LocationList";
    const transactionId = new HttpParams().append("transactionId", newUuid);
    return this.http.post(url, data, { params: transactionId });
  }

  getStaffLocationList(staffId: string): Observable<any> {
    if (!staffId) throw new Error("Staff ID is missing");
    const url = "/api/Personnel/LocationList/" + staffId;
    return this.http.get(url);
  }

  updateStaffLockOpeningRights(
    staffId: string,
    data: any,
    newUuid: string
  ): Observable<any> {
    const url = `/api/Personnel/${staffId}/LockOpeningRights`;
    // console.log("updateStaffLockOpeningRights", newUuid);
    const transactionId = new HttpParams().append("transactionId", newUuid);
    return this.http.post(url, data, { params: transactionId });
  }

  getStaffLockOpeningRights(staffId: string): Observable<any> {
    const url = `/api/Personnel/${staffId}/LockOpeningRights`;
    return this.http.get(url);
  }

  getStaffTemporaryCodeAccess(staffId: string): Observable<any> {
    const url = `/api/Personnel/${staffId}/TemporaryCodeAccess`;
    return this.http.get(url);
  }

  /**
   * ALARM ROUTES
   */

  getAlarmRoutes(): Observable<any> {
    const url = "/api/AlarmRoute";
    return this.http.get(url);
  }

  getAlarmRoutesForDevice(id: string): Observable<any> {
    const url = "/api/AlarmRoute/Device/" + id;
    return this.http.get(url);
  }

  getAlarmRouteDetails(id: string): Observable<any> {
    const url = "/api/AlarmRoute/" + id;
    return this.http.get(url);
  }

  addAlarmRoute(routeData: any): Observable<any> {
    const url = "/api/AlarmRoute";
    return this.http.put(url, routeData);
  }

  updateAlarmRoute(data: any): Observable<any> {
    const url = "/api/AlarmRoute";
    return this.http.post(url, data);
  }

  deleteAlarmRoute(id: string): Observable<any> {
    const url = "/api/AlarmRoute/" + id;
    return this.http.delete(url);
  }

  updatePersonnelAlarmRoutes(
    personnelId: string,
    data: any,
    newUuid: string
  ): Observable<any> {
    const url = "/api/AlarmRoute/Recipients/" + personnelId;
    // console.log("updatePersonnelAlarmRoutes", newUuid);
    const transactionId = new HttpParams().append("transactionId", newUuid);
    return this.http.post(url, data, { params: transactionId });
  }

  updateDeviceAlarmRoutes(data: any): Observable<any> {
    const url = "/api/AlarmRoute/Devices/UpdateActivation";
    return this.http.post(url, data);
  }

  /**
   * CALL CENTER
   */

  getCallCenters(): Observable<any> {
    const url = "/api/CallCenter/";
    return this.http.get(url);
  }

  //** USER GROUPS */

  updateUserGroups(groupIds: any): Observable<any> {
    const url = "/api/AlarmRoute";
    return this.http.post(url, groupIds);
  }

  getUsersLoggedInGroups(): Observable<any> {
    const url = "/api/User";
    return this.http.get(url);
  }

  /**
   * RECEIVER TEAMS
   */

  getReceiverTeams(): Observable<any> {
    const url = "/api/AlarmRoute/UserGroups";
    return this.http.get(url);
  }

  getAllMainUserReceiverTeams(): Observable<any> {
    const url = "/api/AlarmRoute/MainUser/UserGroups";
    return this.http.get(url).pipe(map((res) => res ?? []));
  }

  addReceiverTeam(groupData: any): Observable<any> {
    const url = "/api/AlarmRoute/UserGroup";
    return this.http.put(url, groupData);
  }

  editReceiverTeam(groupData: any): Observable<any> {
    const url = "/api/AlarmRoute/UserGroup";
    return this.http.post(url, groupData);
  }

  deleteReceiverTeam(groupId: string): Observable<any> {
    const url = "/api/AlarmRoute/UserGroup/" + groupId;
    return this.http.delete(url);
  }

  addUserToReceiverTeam(groupId: string): Observable<any> {
    const url = "/api/AlarmRoute/UserGroups/User/" + groupId;
    return this.http.put(url, "");
  }

  deleteUserFromReceiverTeam(groupId: string): Observable<any> {
    const url = "/api/AlarmRoute/UserGroups/User/" + groupId;
    return this.http.delete(url);
  }

  changeUserActiveStatus(data: any): Observable<any> {
    const url = "/api/AlarmRoute/UserGroups/User/ActiveStatus";
    return this.http.post(url, data);
  }

  updatePersonnelReceiverTeams(
    personnelId: string,
    data: any,
    newUuid: string
  ): Observable<any> {
    const url = "/api/AlarmRoute/UserGroups/User/" + personnelId;
    // console.log("updatePersonnelReceiverTeams", newUuid);
    const transactionId = new HttpParams().append("transactionId", newUuid);
    return this.http.post(url, data, { params: transactionId });
  }

  getLocationLockAccess(groupId: string): Observable<any> {
    const url = "/api/LockDevice/receiverTeam/" + groupId + "/openingRights";
    return this.http.get(url);
  }

  updateLocationLockAccess(groupId: string, data: string[]): Observable<any> {
    const url = "/api/LockDevice/receiverTeam/" + groupId + "/openingRights";
    return this.http.post(url, data);
  }

  /**
   * LOCK AND BEACON
   */

  getLockAndBeaconConfig(id: string): Observable<any> {
    if (!this.lockBeaconConfigCache.has(id)) {
      const url = "/api/LockDevice/" + id;
      const request$ = this.http.get(url).pipe(
        catchError((err) => {
          this.lockBeaconConfigCache.delete(id); // evict on error so next call retries
          return throwError(() => err);
        }),
        shareReplay(1),
      );
      this.lockBeaconConfigCache.set(id, request$);
    }
    return this.lockBeaconConfigCache.get(id)!;
  }

  saveLockAndBeaconConfig(data: LockBeaconModel): Observable<any> {
    this.lockBeaconConfigCache.delete(data.deviceId);
    const url = "/api/LockDevice";
    return this.http.post(url, data);
  }

  /**
   * REGISTRY
   */

  getMobileWhitelist(): Observable<any> {
    const url = "/api/Registry/Mobile";
    return this.http.get(url);
  }

  approveWhitelistRequest(mobileId: string): Observable<any> {
    const url = "/api/Registry/Mobile/" + mobileId + "/Approve";
    return this.http.put(url, null);
  }

  denyWhitelistRequest(mobileId: string): Observable<any> {
    const url = "/api/Registry/Mobile/" + mobileId + "/Deny";
    return this.http.put(url, null);
  }

  updateMobile(mobileId: string, data: any): Observable<any> {
    const url = "/api/Registry/Mobile/" + mobileId + "/Update";
    return this.http.put(url, data);
  }

  /**
   * RESIDENTS
   */
  getResidentList(
    pageSize: number,
    pageIndex: number,
    sortKey: NewResidentSorter,
    sortBy: SortOrder,
    search: string = ""
  ): Observable<IResidentList> {
    const url = `/api/Resident/GetResidentList/${pageSize}/${pageIndex}/${sortKey}/${sortBy}`;
    const params = new HttpParams().append("search", search);
    return this.http.get<IResidentList>(url, { params });
  }

  getCompanyGroups(): Observable<any> {
    const url = "/api/Resident/GetCompanyGroups";
    return this.http.get(url);
  }

  setResidentCompanyLocation(data: ResidentCompany[]): Observable<any> {
    const url = "/api/Resident/SetResidentCompanyLocation";
    return this.http.post(url, data);
  }

  setResidentDisplayName(data: ResidentDisplayName): Observable<any> {
    const url = "/api/Resident/SetResidentDisplayName";
    return this.http.post(url, data);
  }

  getCompanyFlaggedResidents(
    pageSize: number,
    pageIndex: number,
    sortBy: SortOrder,
    sortKey: FlaggedResidentSorter
  ): Observable<any> {
    const url = `/api/Resident/GetCompanyFlaggedResidents/${sortBy}/${sortKey}/${pageSize}/${pageIndex}`;
    return this.http.get(url);
  }

  deleteResident(data: ResidentCompany): Observable<any> {
    const url =
      "/api/Resident/DeleteResident/" + data.residentId + "/" + data.companyId;
    return this.http.delete(url);
  }

  getCompanyResidents(residentActivity: number): Observable<any> {
    const url = "/api/Resident/GetCompanyResidents/" + residentActivity;
    return this.http.get(url);
  }

  setResidentDevice(data: SetResidentDevice): Observable<any> {
    const url = "/api/Resident/SetResidentDevice";
    return this.http.post(url, data);
  }

  getResidentDevices(residentId: number): Observable<any> {
    const url = "/api/Resident/GetResidentDevices/" + residentId;
    return this.http.get(url);
  }

  unsetResidentDevice(data: UnsetResidentDevice): Observable<any> {
    const url = "/api/Resident/UnsetResidentDevice";
    return this.http.post(url, data);
  }

  getCompanyResidentDevices(): Observable<any> {
    const url = "/api/Resident/GetCompanyResidentDevices/";
    return this.http.get(url);
  }

  getResidentDevicesByCompany(companyId: string): Observable<any> {
    const url = "/api/Resident/GetCompanyResidentDevices/" + companyId;
    return this.http.get(url);
  }

  confirmResidentChanges(data: ResidentCompany[]): Observable<any> {
    const url = "/api/Resident/ConfirmResidentChanges/";
    return this.http.post(url, data);
  }

  getCompanyGroupAllResidents(
    page: number,
    search: string,
    pageSize?: number,
    sortKey?: AllResidentSorter,
    sortOrder?: SortOrder
  ): Observable<any> {
    const url = "/api/Resident/GetCompanyGroupAllResidents/" + page;
    let params = new HttpParams();
    params = params.append("search", search);
    if (pageSize) params = params.append("pageSize", pageSize);
    if (sortKey) params = params.append("sortKey", sortKey);
    if (sortOrder) params = params.append("sortBy", sortOrder);
    return this.http.get(url, { params });
  }

  getCompaynGroupResidentAndDeviceStatistics(): Observable<any> {
    const url = "/api/Resident/GetCompanyGroupResidentAndDeviceStatistics";
    return this.http.get(url);
  }

  /**
   * COMPANY
   */

  getCompanyList(): Observable<any> {
    const url = "/api/Company/List";
    return this.http.get(url);
  }

  getCompany(companyId: string, serverName: string): Observable<any> {
    const url = "/api/Company/" + companyId;
    const server = new HttpParams().append("server", serverName);
    return this.http.get(url, { params: server });
  }

  getCompanySummary(companyId: string, serverName: string): Observable<any> {
    const url = `/api/Company/${serverName}/${companyId}/Summary`;
    return this.http.get(url);
  }

  addCompany(data: any): Observable<any> {
    const url = "/api/Company/Add";
    return this.http.post(url, data);
  }

  updateCompany(data: any): Observable<any> {
    const url = "/api/Company/Update";
    return this.http.post(url, data);
  }

  deleteCompany(companyId: string, serverName: string): Observable<any> {
    const url = "/api/Company/Delete/" + serverName + "/" + companyId;
    return this.http.delete(url);
  }

  /**
   * COMPANY GROUPS
   */

  getCompanyGroupAndServerLists(): Observable<any> {
    const url = "/api/Company/List/CompanyGroupAndServer";
    return this.http.get(url);
  }

  getCompaniesByCompanyGroup(): Observable<any> {
    const url = "/api/Company/List/CompaniesByCompanyGroup";
    return this.http.get(url);
  }

  addCompanyGroup(data: any): Observable<any> {
    const url = "/api/Company/AddCompanyGroup";
    return this.http.post(url, data);
  }

  updateCompanyGroup(data: any): Observable<any> {
    const url = "/api/Company/UpdateCompanyGroup";
    return this.http.post(url, data);
  }

  // Get companies that doesn't belong to any company group
  getAvailableCompanies(server: string): Observable<any> {
    const url = "/api/Company/GetAvailableCompanies/" + server;
    return this.http.get(url);
  }

  updateCompanyGroupBindingTime(
    data: IUpdateCompanyGroupBindingTimeForm
  ): Observable<void> {
    const url = "/api/Company/SaveCompanyGroupBindingTime";
    return this.http.put<void>(url, data);
  }

  updateCompanyGroupLogoutUrl(data: { ServerIdCompanyGroupId: string; logoutUrl: string }) {
    return this.http.put<void>("/api/Company/SaveCompanyGroupLogoutUrl", data);
  }

  addCompanyToGroup(
    companies: ICompanyGroupCompany[],
    companyGroupId: string,
    companyGroupServer: string
  ): Observable<any> {
    const url = `/api/Company/AddCompaniesToGroup/${companyGroupId}/${companyGroupServer}`;
    return this.http.post(url, companies);
  }

  removeCompanyFromGroup(server: string, companyId: string): Observable<any> {
    const url = `/api/Company/RemoveCompanyFromGroup/${server}/${companyId}`;
    return this.http.post(url, {});
  }

  getCompanyGroupBindingTimeConfig(): Observable<BindingTimeConfig> {
    const url = "/api/Company/Group/BindingTimeConfiguration";
    return this.http.get<BindingTimeConfig>(url);
  }

  /**
   * @param page Page number
   * @param sortOrder 1 = ascending order, -1 descending order
   * @param search string
   * @returns staff list with optional search and sort completed
   */

  getCompanyGroupNewStaff(
    staffPageIndex: number,
    itemsPerPage?: number,
    search?: string,
    sortOrder?: number
  ): Observable<any> {
    const url = "/api/Staff/GetCompanyGroupNewStaff/" + staffPageIndex;
    let params = new HttpParams();
    if (itemsPerPage) params = params.append("itemsPerPage", itemsPerPage);
    if (search) params = params.append("search", search);
    if (sortOrder) params = params.append("sortOrder", sortOrder);
    return this.http.get(url, { params: params });
  }

  // Get paginated list of staff for the company group landing page with optional search and sort
  getCompanyGroupLandingPageStaff(
    staffPageIndex: number,
    itemsPerPage?: number,
    search?: string,
    sortOrder?: number
  ): Observable<any> {
    const url = "/api/Staff/GetCompanyGroupLandingPageStaff/" + staffPageIndex;
    let params = new HttpParams();
    if (itemsPerPage) params = params.append("itemsPerPage", itemsPerPage);
    if (search) params = params.append("search", search);
    if (sortOrder) params = params.append("sortOrder", sortOrder);
    return this.http.get(url, { params: params });
  }

  // Get all non-federated staff for the company group
  getCompanyGroupNonFedStaff(): Observable<any> {
    const url = "/api/Staff/GetCompanyGroupNonFedStaff";
    return this.http.get(url);
  }

  getStaffCompanyLocations(identityId: string): Observable<any> {
    const url = "/api/Staff/StaffCompanyLocations";
    const params = new HttpParams().append("identityId", identityId);
    return this.http.get(url, { params: params });
  }

  setStaffCompanyLocation(data: NewStaff[]): Observable<any> {
    const url = "/api/Staff/SetStaffCompanyLocation";
    return this.http.post(url, data);
  }

  deleteStaffCompanyLocation(id: string): Observable<any> {
    const url = "/api/Staff/RemoveCompany/" + id;
    return this.http.delete(url);
  }

  updateNewStaffRole(staffRole: number, identityId: string): Observable<any> {
    const url = "/api/Staff/UpdateNewStaffRole";
    return this.http.put(url, { identityId, staffRole });
  }

  createLandingPageUser(req: CreateLandingPageUserRequest): Observable<any> {
    const url = "/api/Staff/CreateLandingPageUser";
    return this.http.post(url, req);
  }

  updateLandingPageUser(req: any): Observable<any> {
    const url = "/api/Staff/UpdateLandingPageUser";
    return this.http.put(url, req);
  }

  deleteLandingPageUser(identityId: string): Observable<any> {
    return this.http.delete(
      `/api/Staff/DeleteLandingPageUser/${identityId}`
    );
  }

  /**
   * IOTCOMMS
   */
  getIotCommsConfigurationForLocation(id: string): Observable<any> {
    const url = "/api/IotComms/Configuration/" + id;
    return this.http.get(url);
  }

  setIotCommsConfigurationForLocation(data: any): Observable<any> {
    const url = "/api/IotComms/Configuration";
    return this.http.post(url, data);
  }

  syncLocationToIotComms(locationId: string): Observable<any> {
    const url = "/api/IotComms/Provision/" + locationId;
    return this.http.get(url);
  }

  /** Alarm Reason */
  getAlarmReasons(langId: string): Observable<any> {
    const url = "/api/AlarmReason/List/" + langId;
    return this.http.get(url);
  }

  setReasonsToAlert(data: IAlarmReasonsRequest): Observable<any> {
    const url = "/api/AlarmReason/SaveToAlert";
    return this.http.post(url, data);
  }

  saveAlarmReasons(data: IAlarmReasonCategory[]): Observable<any> {
    const url = "/api/AlarmReason/Save";
    return this.http.post(url, data);
  }

  deleteAlarmReasonCategory(category: {
    dictionaryEntryKey: string;
    alarmReasonCategoryID: number;
  }): Observable<any> {
    const url = `/api/AlarmReason/Delete/Category/${category.dictionaryEntryKey}/${category.alarmReasonCategoryID}`;
    return this.http.delete(url);
  }

  deleteAlarmReason(reason: {
    dictionaryEntryKey: string;
    alarmReasonCategoryID: number;
    alarmReasonID: number;
  }): Observable<any> {
    const url = `/api/AlarmReason/Delete/Reason/${reason.dictionaryEntryKey}/${reason.alarmReasonCategoryID}/${reason.alarmReasonID}`;
    return this.http.delete(url);
  }

  createNewAlert(
    deviceId: string,
    group: number,
    node: number
  ): Observable<any> {
    const url = "/api/Alert";
    const data = {
      deviceID: deviceId,
      alarmActivationGroup: group,
      alarmActivationNode: node,
    };
    return this.http.put(url, data);
  }

  getAlarmHistory(deviceId: string, userId: string, customerId: string, locationId: string): Observable<AlertHistory[]> {
    const url = `/api/Alert/History/${deviceId}`;
    const params = { userId, customerId, locationId };
    return this.http.get<AlertHistory[]>(url, { params });
  }

  /** CUSTOMER RULES AND CALL LIST INFORMATION */
  getCompanyRules(customerId: string, server: string): Observable<CustomerRule[]> {
    if (!server) throw new Error('server missing');

    return this.http.get<CustomerRule[]>(
      `/api/Customer/${customerId}/Rules`,
      { params: { server } }
    );
  }

  saveCustomerRules(
    customerId: string,
    body: CustomerRuleUpdate
  ): Observable<void> {
    const url = `/api/Admin/CustomerRule/${customerId}`;
    return this.http.put<void>(url, body);
  }

  getCustomerCallList(customerId: string, server: string) {
    if (!server) throw new Error('server missing');

    return this.http.get<CallListItem[]>(
      `/api/Customer/${customerId}/CallList`,
      { params: { server } }
    );
  }

  saveCustomerCallList(customerId: string, body: CustomerCallListUpdate) {
    return this.http.put(
      `/api/Admin/CustomerCallList/${customerId}`,
      body
    );
  }

  /** ACTIVITY LOG */
  getUserFilters(): Observable<IGroupedOption[]> {
    const url = "/api/ActivityLog/GetUserFilters";
    return this.http.get<IGroupedOption[]>(url);
  }

  getActivityLogs(filters: ActivityLogFilters): Observable<IActivityLog[]> {
    const url = "/api/ActivityLog";
    return this.http.post<IActivityLog[]>(url, filters).pipe(
      map((data) => {
        !data && (data = []);

        // Validate each item in the array
        data.forEach((item) => {
          const error = this.validateActivityLog(item);

          /* Uncommented log errors, as they slow down the browser console.
          if (error) {
            console.error(
              `Data does not match IActivityLog interface. Invalid property: ${error}`
            );
          }
          */
          const pause = item.details?.cameraGroupPauseChanges;
          if (pause) {
            item.details.start = pause.pauseStart?.newValue ?? undefined;
            item.details.end = pause.pauseEnd?.newValue ?? undefined;
          }

          // HTML-encode user-controlled fields before they reach pipes that use bypassSecurityTrustHtml
          item.staffName = this.htmlEncodeActivityField(item.staffName);
          if (item.details) {
            item.details.targetName = this.htmlEncodeActivityField(item.details.targetName);
            item.details.targetId = this.htmlEncodeActivityField(item.details.targetId);
          }
        });
        return data;
      })
    );
  }

  private htmlEncodeActivityField(input: string | null | undefined): string | null | undefined {
    if (input == null) return input;
    return String(input)
      .replace(/&/g, '&amp;')   // Must be first to avoid double-encoding
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private validateActivityLog(log: any): string | null {
    if (typeof log.id !== "string") return "id";
    if (typeof log.timestamp !== "string") return "timestamp";
    if (typeof log.companyId !== "string") return "companyId";
    if (typeof log.staffId !== "string") return "staffId";
    if (typeof log.staffName !== "string") return "staffName";
    if (typeof log.area !== "string") return "area";
    const detailsError = this.validateActivityDetails(log.details);
    if (detailsError) return `details.${detailsError}`;
    if (typeof log.action !== "string") return "action";
    return null;
  }

  private validateActivityDetails(details: any): string | null {
    if (details == null) return "details";
    if (typeof details.translationCode !== "string") return "translationCode";
    if (typeof details.targetName !== "string") return "targetName";
    if (typeof details.targetId !== "string") return "targetId";
    return null;
  }

  //**VOIP */
  provisionVoip(data: any): Observable<any> {
    const url = "/api/VoIP/Provision";
    return this.http.post(url, data);
  }

  deprovisionVoip(devId: string): Observable<any> {
    const url = "/api/VoIP/Provision/" + devId;
    return this.http.delete(url);
  }

  //** GLOBAL LOCAL FUNCTIONS */

  getDeviceType(type: string, name: boolean = true): string {
    let devTypeList = localStorage.getItem("devType");
    if (!devTypeList && localStorage.getItem("customerInfo")) {
      this.getDeviceTranslations(localStorage.getItem("language")).subscribe(
        (res) => {
          localStorage.setItem("devType", JSON.stringify(res));
          devTypeList = res;
        }
      );
    }
    const array = JSON.parse(localStorage.getItem("devType"));
    if (array) {
      for (let i = 0; i < array.length; i++) {
        if (array[i].deviceType === type) {
          if (array[i].deviceTypeName && name) {
            return (
              array[i].deviceTypeName + " (" + array[i].deviceTypeCode + ")"
            );
          } else {
            return array[i].deviceTypeCode;
          }
        }
      }
    }
    return "";
  }

  getDeviceIconClass(devType: string) {
    //First match exact device types
    switch (devType) {
      case "10-5":
      case "10-9":
        return "fal fa-door-closed";
      case "10-2":
      case "89-1":
      case "88-1":
        return "fal fa-fire-alt";
      case "32-6":
      case "32-8":
        return "fal fa-lock-alt";
      case "10-12":
      case "09-1":
      case "10-15":
        return "fal fa-lightbulb-on";
      case "32-1":
      case "32-3":
        return "fal fa-broadcast-tower";
      case "31-8":
      case "31-7":
        return "fal fa-tv";
      case "10-14":
        return "fal fa-memory";
      case "10-3":
        return "fal fa-video";
      case "13-1":
        return "fal fa-draw-circle";
      case "18-192":
        return "fal fa-chart-network";
      case "E8-2":
        return "fab fa-bluetooth-b";
      case "E8-3":
        return "fal fa-plug";
      default:
        break;
    }
    //Finally match only device prefixes
    switch (devType.substring(0, 2)) {
      case "04":
      case "05":
        return "fal fa-dot-circle";
      case "06":
      case "07":
        return "fal fa-location";
      case "34":
        return "fal fa-video";
      case "D1":
      case "E1":
        return "fal fa-circle";
      case "33":
        return "fal fa-mobile";
      case "F0":
        return "fal fa-key";
      case "31":
        return "fal fa-microchip";
      case "08":
        return "fal fa-tag";
      case "36":
        return "fal fa-phone-square";
      default:
        break;
    }
    return "";
  }

  /** TEMP CODE */
  getTemporaryCode(deviceID: string): Observable<any> {
    const url = "/api/LockDevice/Locks/" + deviceID + "/TemporaryCode";
    return this.http.get<any>(url);
  }
}
